import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
  Pin,
} from "@vis.gl/react-google-maps";
import { formatCurrency } from "../../CommonUtils/currencyUtils";
import { useMediaQuery } from "@mui/material";
import styles from "./MapView.module.css";
import {
  EMPOWERMENT_PIN_COLORS,
  EMPOWERMENT_ZONE_TYPES,
  getEmpowermentZoneCategory,
} from "../../CommonUtils/empowermentZones";

const MapView = ({
  businesses = [],
  selectedBusinessIds = [],
  onMarkerSelect,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);

  const DEFAULT_ZOOM = 4;
  const SINGLE_MARKER_ZOOM = 11;

  const selectedIds = useMemo(() => {
    const ids = new Set();
    (selectedBusinessIds || [])
      .filter((id) => id !== null && id !== undefined && id !== "")
      .forEach((id) => ids.add(String(id)));
    return ids;
  }, [selectedBusinessIds]);

  // Container style for the map
  const containerStyle = {
    width: "100%",
    height: "100%",
  };

  // Default center of the US
  const defaultCenter = {
    lat: 37.0902,
    lng: -95.7129,
  };

  // Process businesses when they change
  useEffect(() => {
    const initialProcessedBusinesses = processBusinesses(businesses);
    setFilteredBusinesses(initialProcessedBusinesses);
    findBusinessesWithSameLatLang(initialProcessedBusinesses);
  }, [businesses]);

  // Handle map load
  const onLoad = useCallback(
    function callback(map) {
      // Fit bounds to include all markers when map loads
      if (filteredBusinesses && filteredBusinesses.length > 0) {
        fitMapToBounds(map, filteredBusinesses);
      }
    },
    [filteredBusinesses]
  );

  // Utility function to fit map to markers bounds
  const fitMapToBounds = (map, locations) => {
    if (!map) return;
    if (!locations || locations.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    locations.forEach((location) => {
      if (location.lat && location.lng) {
        bounds.extend({
          lat: parseFloat(location.lat),
          lng: parseFloat(location.lng),
        });
      }
    });

    map.fitBounds(bounds);

    if (locations.length === 1) {
      const [only] = locations;
      map.setCenter({
        lat: parseFloat(only.lat),
        lng: parseFloat(only.lng),
      });
      map.setZoom(SINGLE_MARKER_ZOOM);
    }
  };

  // Process businesses data
  const processBusinesses = (businessesData) => {
    return businessesData.map((business) => {
      const totalProjectedTaxCredits = calculateProjectedTaxCredits(
        business.locationEmployeesCount,
        business.taxCredits
      );
      return {
        ...business,
        // totalProjectedTaxCredits,
      };
    });
  };

  // Calculate projected tax credits
  const calculateProjectedTaxCredits = (
    locationEmployeesCount,
    taxCreditsArray
  ) => {
    let totalProjectedTaxCredits = 0;
    taxCreditsArray.forEach((taxCreditGroup) => {
      taxCreditGroup.availableTaxCredits.forEach((taxCredit) => {
        const factor =
          taxCredit.name.toLowerCase() === "wotc" ||
          taxCredit.name.toLowerCase() === "work opportunity tax credit"
            ? 0.2
            : 0.35;
        totalProjectedTaxCredits += locationEmployeesCount * factor;
      });
    });

  return totalProjectedTaxCredits;
};

const resolveBusinessIdentifier = (business) =>
  business?.prospectBusinessId || business?.businessId || business?.id || null;

  // Determine marker colors based on empowerment zone
  const getMarkerColors = (companies = []) => {
    if (!Array.isArray(companies) || companies.length === 0) {
      return EMPOWERMENT_PIN_COLORS[EMPOWERMENT_ZONE_TYPES.NONE];
    }
    const categories = new Set(
      companies.map((company) =>
        getEmpowermentZoneCategory(company.empowermentZone)
      )
    );
    if (categories.size === 1) {
      const [category] = Array.from(categories);
      return (
        EMPOWERMENT_PIN_COLORS[category] ||
        EMPOWERMENT_PIN_COLORS[EMPOWERMENT_ZONE_TYPES.NONE]
      );
    }
    if (categories.has(EMPOWERMENT_ZONE_TYPES.EMPOWERMENT)) {
      return EMPOWERMENT_PIN_COLORS[EMPOWERMENT_ZONE_TYPES.EMPOWERMENT];
    }
    if (categories.has(EMPOWERMENT_ZONE_TYPES.RURAL)) {
      return EMPOWERMENT_PIN_COLORS[EMPOWERMENT_ZONE_TYPES.RURAL];
    }
    return EMPOWERMENT_PIN_COLORS[EMPOWERMENT_ZONE_TYPES.NONE];
  };

  // Find businesses with same coordinates
  const findBusinessesWithSameLatLang = (companies) => {
    const combinedCompanies = [];
    const MapConstructor =
      typeof window !== "undefined" && window.Map ? window.Map : Map;
    const companyMap = new MapConstructor();

    companies.forEach((company) => {
      const { lat, lng, ...rest } = company;
      const key = `${lat}-${lng}`;

      if (companyMap.has(key)) {
        const existingCompany = companyMap.get(key);
        existingCompany.commonCompanies.push(rest);
      } else {
        companyMap.set(key, {
          lat,
          lng,
          commonCompanies: [rest],
        });
      }
    });

    companyMap.forEach((value) => {
      combinedCompanies.push(value);
    });

    setFilteredBusinesses(combinedCompanies);
    return combinedCompanies;
  };

  // Handle marker click
  const handleMarkerClick = (business) => {
    setSelectedMarker(business);
    if (
      typeof onMarkerSelect === "function" &&
      Array.isArray(business?.commonCompanies) &&
      business.commonCompanies.length === 1
    ) {
      onMarkerSelect(business.commonCompanies[0]);
    }
  };

  // Close info window
  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };

  // Map bounds fit component
  const MapFitter = ({ businesses }) => {
    const map = useMap();

    useEffect(() => {
      if (map && businesses && businesses.length > 0) {
        fitMapToBounds(map, businesses);
      }
    }, [map, businesses]);

    return null;
  };

  return (
    <div
      style={{ height: "100%" }}
      className={isMobile ? styles.borderCls : ""}
    >
      <APIProvider apiKey="AIzaSyA_9y02Rkq6YDfAuGnRkCFsFkZIxJhW5UA">
        <Map
          mapId="fd16af6e873de8bf6283afde" // Create a map style in Google Cloud Console
          style={containerStyle}
          defaultZoom={DEFAULT_ZOOM}
          defaultCenter={defaultCenter}
          onLoad={onLoad}
          gestureHandling={"greedy"}
          disableDefaultUI={false}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          }}
        >
          {filteredBusinesses.map((business, index) => {
            if (!(business.lat && business.lng)) {
              return null;
            }
            const markerSelected =
              Array.isArray(business.commonCompanies) &&
              business.commonCompanies.some((comp) => {
                const compId = resolveBusinessIdentifier(comp);
                return compId && selectedIds.has(String(compId));
              });
            const pinScale = markerSelected ? 1.4 : 1.1;
            const pinColors = getMarkerColors(business.commonCompanies);
            const borderColor = markerSelected
              ? "#000000"
              : pinColors.borderColor;

            return (
              <AdvancedMarker
                key={`${index}-${business.lat}-${business.lng}`}
                position={{
                  lat: parseFloat(business.lat),
                  lng: parseFloat(business.lng),
                }}
                onClick={() => handleMarkerClick(business)}
                title={business.commonCompanies
                  .map((comp) => comp.businessName)
                  .join(", ")}
                options={{
                  animation: 2, // DROP animation (equivalent to google.maps.Animation.DROP)
                }}
              >
                <Pin
                  background={pinColors.background}
                  borderColor={borderColor}
                  glyphColor={pinColors.glyphColor}
                  scale={pinScale}
                />
              </AdvancedMarker>
            );
          })}

          {selectedMarker && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedMarker.lat),
                lng: parseFloat(selectedMarker.lng),
              }}
              onCloseClick={handleInfoWindowClose}
            >
              <div className={styles.infoWindow}>
                {selectedMarker.commonCompanies.map((commonComp, cIndex) => (
                  <div
                    key={cIndex}
                    className={`${styles.infoWindowCard} ${
                      selectedIds.has(
                        String(resolveBusinessIdentifier(commonComp))
                      )
                        ? styles.infoWindowCardSelected
                        : ""
                    }`}
                    onClick={() => {
                      if (typeof onMarkerSelect === "function") {
                        onMarkerSelect(commonComp);
                      }
                    }}
                  >
                    <h6 className="font-700" style={{ margin: "0 0 8px 0" }}>
                      {commonComp.businessName}
                    </h6>
                    <div
                      className={styles.popup_available_credits}
                      style={{ display: "flex", marginBottom: "5px" }}
                    >
                      <div
                        className="col-6 fw-bold text-grey font-12"
                        style={{ flexBasis: "50%" }}
                      >
                        Projected Tax credit:{" "}
                      </div>
                      <div
                        className="col-6 font-700 text-grey font-12"
                        style={{ flexBasis: "50%" }}
                      >
                        {commonComp.taxCredits[0].totalProjectedTaxCreditAmount
                          ? "$" +
                            formatCurrency(
                              commonComp.taxCredits[0]
                                .totalProjectedTaxCreditAmount
                            )
                          : "No data available"}{" "}
                      </div>
                    </div>

                    <div
                      className={styles.popup_available_credits}
                      style={{ display: "flex" }}
                    >
                      <div
                        className="col-6 fw-bold text-grey font-12"
                        style={{ flexBasis: "50%" }}
                      >
                        Available Tax Credits:
                      </div>
                      <ol
                        className="col-6 text-grey font-12"
                        style={{
                          paddingLeft: "13px",
                          flexBasis: "50%",
                          margin: "0",
                        }}
                      >
                        {commonComp.taxCredits.flatMap((taxCreditGroup) =>
                          taxCreditGroup.availableTaxCredits.map(
                            (credit, index) => (
                              <li key={index} className="font-700">
                                {credit.name}:{" "}
                                {credit.projectedTaxCreditAmount
                                  ? credit.projectedTaxCreditAmount
                                  : " No data available"}
                              </li>
                            )
                          )
                        )}
                      </ol>
                    </div>
                    {typeof onMarkerSelect === "function" && (
                      <div className={styles.infoWindowHint}>
                        Click to select this business
                      </div>
                    )}
                    {selectedMarker.commonCompanies.length > cIndex + 1 && (
                      <hr style={{ margin: "10px 0" }} />
                    )}
                  </div>
                ))}
              </div>
            </InfoWindow>
          )}

          <MapFitter businesses={filteredBusinesses} />
        </Map>
      </APIProvider>
    </div>
  );
};

export default MapView;
